import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Sparkles, Info } from 'lucide-react';
import { admissionService, bedService, autoAllocationService } from '../../lib/services.ipd';
import { patientService, doctorService } from '../../lib/services';
import type { Bed } from '../../lib/services.ipd';
import toast from 'react-hot-toast';

const admissionSchema = z.object({
  patient_id: z.string().min(1, 'Patient is required'),
  doctor_id: z.string().min(1, 'Doctor is required'),
  bed_id: z.string().min(1, 'Bed is required'),
  admission_type: z.enum(['emergency', 'planned', 'transfer']),
  chief_complaint: z.string().optional(),
  provisional_diagnosis: z.string().optional(),
  notes: z.string().optional(),
});

type AdmissionFormData = z.infer<typeof admissionSchema>;

export default function NewAdmission() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState<any[]>([]);
  const [doctors, setDoctors] = useState<any[]>([]);
  const [availableBeds, setAvailableBeds] = useState<Bed[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoSuggest, setAutoSuggest] = useState(false);
  const [suggestedBed, setSuggestedBed] = useState<Bed | null>(null);
  const [suggesting, setSuggesting] = useState(false);

  const { register, handleSubmit, setValue, formState: { errors, isSubmitting } } = useForm<AdmissionFormData>({
    resolver: zodResolver(admissionSchema),
    defaultValues: {
      admission_type: 'planned',
    }
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [patientsData, doctorsData, bedsData] = await Promise.all([
        patientService.getAll(),
        doctorService.getAll(),
        bedService.getAvailable(),
      ]);
      setPatients(patientsData);
      setDoctors(doctorsData);
      setAvailableBeds(bedsData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoSuggest = async () => {
    if (!autoSuggest) return;
    
    setSuggesting(true);
    try {
      const suggested = await autoAllocationService.suggestBed();
      if (suggested) {
        setSuggestedBed(suggested);
        setValue('bed_id', suggested.id);
        toast.success(`Suggested: ${suggested.bed_no} - ${suggested.ward?.name} (₹${suggested.daily_rate}/day)`);
      } else {
        toast.error('No available beds found');
        setSuggestedBed(null);
      }
    } catch (error) {
      console.error('Failed to suggest bed:', error);
      toast.error('Failed to suggest bed');
    } finally {
      setSuggesting(false);
    }
  };

  useEffect(() => {
    if (autoSuggest && availableBeds.length > 0) {
      handleAutoSuggest();
    }
  }, [autoSuggest]);

  const onSubmit = async (data: AdmissionFormData) => {
    try {
      const { bed_id, ...admissionData } = data;
      
      const admission = await admissionService.create({
        ...admissionData,
        admission_date: new Date().toISOString(),
        status: 'admitted',
      });
      
      await bedService.assignBed(admission.id, bed_id);
      
      toast.success('Admission created successfully');
      navigate(`/ipd/${admission.id}`);
    } catch (error) {
      console.error('Failed to create admission:', error);
      toast.error('Failed to create admission');
    }
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/ipd')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Admissions
        </button>
        <h1 className="text-2xl font-bold text-gray-900">New Admission</h1>
        <p className="text-gray-600">Create a new patient admission</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Patient *
              </label>
              <select
                {...register('patient_id')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Patient</option>
                {patients.map(patient => (
                  <option key={patient.id} value={patient.id}>
                    {patient.name}
                  </option>
                ))}
              </select>
              {errors.patient_id && (
                <p className="mt-1 text-sm text-red-600">{errors.patient_id.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Doctor *
              </label>
              <select
                {...register('doctor_id')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Doctor</option>
                {doctors.map(doctor => (
                  <option key={doctor.id} value={doctor.id}>
                    {doctor.name} - {doctor.specialization}
                  </option>
                ))}
              </select>
              {errors.doctor_id && (
                <p className="mt-1 text-sm text-red-600">{errors.doctor_id.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admission Type *
              </label>
              <select
                {...register('admission_type')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="planned">Planned</option>
                <option value="emergency">Emergency</option>
                <option value="transfer">Transfer</option>
              </select>
              {errors.admission_type && (
                <p className="mt-1 text-sm text-red-600">{errors.admission_type.message}</p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Bed *
                </label>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoSuggest}
                      onChange={(e) => {
                        setAutoSuggest(e.target.checked);
                        if (!e.target.checked) {
                          setSuggestedBed(null);
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <Sparkles className="w-4 h-4 text-blue-600" />
                    Auto-suggest available bed
                  </label>
                  {!autoSuggest && (
                    <button
                      type="button"
                      onClick={handleAutoSuggest}
                      disabled={suggesting}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100 disabled:opacity-50"
                    >
                      <Sparkles className="w-3 h-3" />
                      Suggest
                    </button>
                  )}
                </div>
              </div>
              
              {suggestedBed && (
                <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-900">Suggested Bed:</p>
                      <p className="text-blue-700">
                        {suggestedBed.bed_no} - {suggestedBed.ward?.name}
                      </p>
                      <p className="text-blue-600 text-xs mt-1">
                        Type: {suggestedBed.bed_type} • Rate: ₹{suggestedBed.daily_rate}/day • Floor: {suggestedBed.ward?.floor}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <select
                {...register('bed_id')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                disabled={suggesting}
              >
                <option value="">Select Bed</option>
                {availableBeds.map(bed => (
                  <option key={bed.id} value={bed.id}>
                    {bed.bed_no} - {bed.ward?.name} ({bed.bed_type}) - ₹{bed.daily_rate}/day
                  </option>
                ))}
              </select>
              {errors.bed_id && (
                <p className="mt-1 text-sm text-red-600">{errors.bed_id.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Chief Complaint
            </label>
            <textarea
              {...register('chief_complaint')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Enter the chief complaint..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Provisional Diagnosis
            </label>
            <textarea
              {...register('provisional_diagnosis')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Enter the provisional diagnosis..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              {...register('notes')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Enter any additional notes..."
            />
          </div>

          <div className="flex gap-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create Admission'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/ipd')}
              className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
